/**
 * Provisioning Service
 * Handles CSV upload and job monitoring
 */
import env from "@perfectserve/env-ps";
import { createCSVFile, generateCSVFilename } from "../utils/csvGenerator";
// Job status constants
const JOB_STATUS = {
    NOT_STARTED: 0,
    IN_PROGRESS: 1,
    PENDING: 2,
    COMPLETED: 3,
    SCHEDULED: 4,
    ERROR: 5,
};
/**
 * Create standard headers for API requests
 */
function createHeaders(token, contentType) {
    const headers = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json, text/plain, */*",
        "x-perfectserve-ApplicationInfo": '{"Name":"PS-Web","Version":"3.x"}',
        "x-perfectserve-CorrelationId": crypto.randomUUID(),
    };
    if (contentType) {
        headers["Content-Type"] = contentType;
    }
    return headers;
}
/**
 * Create a new provisioning service instance
 * Uses env-ps for configuration
 */
export function createProvisioningService(getAuthToken) {
    // Get configuration from env-ps
    const cspApiBaseUrl = env.cspApi || "";
    const provisioningBaseUrl = env.provisioningApi || "";
    const sourceId = env.provisioningSourceId || "";
    // Determine environment for display purposes
    const environment = cspApiBaseUrl.includes("qa-ps.com") || cspApiBaseUrl.includes("atl.qa") ? "QA" : "PRODUCTION";
    /**
     * Upload CSV file to provisioning system
     */
    async function uploadCSV(records, filename) {
        const csvFile = createCSVFile(records, filename || generateCSVFilename());
        // Create FormData for multipart upload
        const formData = new FormData();
        formData.append("File", csvFile);
        formData.append("SourceId", sourceId);
        formData.append("MakeZendeskTicket", "false");
        try {
            const token = getAuthToken();
            // Use fetch directly for FormData - browser needs to set Content-Type with boundary
            const response = await fetch(`${cspApiBaseUrl}/api/AutoProvision/Practitioner/Upload`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json, text/plain, */*",
                    "x-perfectserve-ApplicationInfo": '{"Name":"PS-Web","Version":"3.x"}',
                    "x-perfectserve-CorrelationId": crypto.randomUUID(),
                },
                body: formData,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                return {
                    success: false,
                    message: errorData.Message || `Upload failed: HTTP ${response.status}`,
                };
            }
            const data = await response.json();
            const jobId = data.jobId || data.JobId;
            return {
                success: true,
                jobId,
                message: data.message || "Upload successful",
            };
        }
        catch (error) {
            return {
                success: false,
                message: error instanceof Error ? error.message : "Upload failed",
            };
        }
    }
    /**
     * Get job status from provisioning system
     */
    async function getJobStatus(jobId) {
        try {
            const token = getAuthToken();
            const response = await fetch(`${provisioningBaseUrl}/api/autoprovision/practitioner/jobs/${jobId}`, {
                headers: createHeaders(token),
            });
            if (!response.ok) {
                console.error(`Job status request failed: ${response.status}`);
                return null;
            }
            return await response.json();
        }
        catch (error) {
            console.error("Error fetching job status:", error);
            return null;
        }
    }
    /**
     * Monitor job status until completion
     */
    async function monitorJobStatus(jobId, onStatusUpdate, maxWaitTime = 300000) {
        const startTime = Date.now();
        const pollInterval = 5000;
        while (Date.now() - startTime < maxWaitTime) {
            const jobData = await getJobStatus(jobId);
            if (!jobData) {
                await new Promise((resolve) => setTimeout(resolve, pollInterval));
                continue;
            }
            // Extract status (API returns capitalized keys)
            const rawStatus = jobData.Status || jobData.status || "Unknown";
            const status = rawStatus.toLowerCase();
            const practitioners = jobData.Practitioners || jobData.practitioners || [];
            // Extract practitioner data including messages
            const createdUsers = practitioners.map((p) => {
                const firstName = p.FirstName || p.firstName || "";
                const lastName = p.LastName || p.lastName || "";
                const credential = p.Credential || p.credential || "";
                const displayName = p.displayName || p.DisplayName || `${lastName}, ${firstName}${credential ? `, ${credential}` : ""}`.trim();
                const messages = (p.Messages || p.messages || []).map((m) => ({
                    id: m.Id || m.id || "",
                    type: m.Type || m.type || "Info",
                    message: m.Message || m.message || "",
                    createdUtc: m.CreatedUtc || m.createdUtc || "",
                }));
                return {
                    userId: p.userId || p.UserId || "",
                    identityId: p.identityId || p.IdentityId || "",
                    displayName,
                    status: p.Status || p.status || "",
                    messages,
                };
            });
            const successfullyCreatedCount = createdUsers.filter((u) => u.status === "Success" || u.status === "success").length;
            const hasWarnings = createdUsers.some((u) => u.status === "Warn" ||
                u.status === "Error" ||
                u.messages?.some((m) => m.type === "Warn" || m.type === "Error"));
            onStatusUpdate(status, createdUsers);
            if (["completed", "complete", "success", "succeeded"].includes(status)) {
                return { success: successfullyCreatedCount > 0, createdUsers, successfullyCreatedCount, hasWarnings };
            }
            if (["failed", "error", "cancelled"].includes(status)) {
                return { success: false, createdUsers, successfullyCreatedCount: 0, hasWarnings };
            }
            await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }
        return { success: false, createdUsers: [], successfullyCreatedCount: 0, hasWarnings: false };
    }
    /**
     * Create a Copy Reference User job
     */
    async function createCopyUserJob(referenceUserId, targetUserIds) {
        try {
            const token = getAuthToken();
            const response = await fetch(`${provisioningBaseUrl}/api/copyUserJobs`, {
                method: "POST",
                headers: createHeaders(token, "application/json"),
                body: JSON.stringify({ referenceUserId, targetUserIds }),
            });
            if (!response.ok) {
                console.error(`Create copy user job failed: ${response.status}`);
                return null;
            }
            const text = await response.text();
            const jobId = text.trim().replace(/"/g, "");
            const guidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
            return guidPattern.test(jobId) ? jobId : null;
        }
        catch (error) {
            console.error("Error creating copy user job:", error);
            return null;
        }
    }
    /**
     * Schedule a Copy Reference User job
     */
    async function scheduleCopyUserJob(jobId) {
        try {
            const token = getAuthToken();
            const response = await fetch(`${provisioningBaseUrl}/api/copyUserJobs/${jobId}/schedule`, {
                method: "POST",
                headers: createHeaders(token),
            });
            return response.ok || response.status === 204;
        }
        catch (error) {
            console.error("Error scheduling copy user job:", error);
            return false;
        }
    }
    /**
     * Get Copy User Job status
     */
    async function getCopyUserJobStatus(jobId) {
        try {
            const token = getAuthToken();
            const response = await fetch(`${provisioningBaseUrl}/api/copyUserJobs/${jobId}/statusReport`, {
                headers: createHeaders(token),
            });
            if (!response.ok) {
                return null;
            }
            return await response.json();
        }
        catch (error) {
            console.error("Error fetching copy user job status:", error);
            return null;
        }
    }
    /**
     * Monitor Copy User Job status until completion
     */
    async function monitorCopyUserJob(jobId, onStatusUpdate, maxWaitTime = 300000) {
        const startTime = Date.now();
        const pollInterval = 5000;
        while (Date.now() - startTime < maxWaitTime) {
            const statusData = await getCopyUserJobStatus(jobId);
            if (!statusData) {
                await new Promise((resolve) => setTimeout(resolve, pollInterval));
                continue;
            }
            const jobStatus = statusData.JobStatus;
            const message = statusData.JobStatusMessage || "";
            onStatusUpdate(jobStatus, message);
            if (jobStatus === JOB_STATUS.COMPLETED || jobStatus === JOB_STATUS.SCHEDULED) {
                return true;
            }
            if (jobStatus === JOB_STATUS.ERROR) {
                return false;
            }
            await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }
        return false;
    }
    /**
     * Execute full Copy Reference User workflow
     */
    async function executeCopyReferenceUser(referenceUserId, targetUserIds, onStatusUpdate) {
        onStatusUpdate("Creating copy user job...");
        const jobId = await createCopyUserJob(referenceUserId, targetUserIds);
        if (!jobId) {
            onStatusUpdate("Failed to create copy user job");
            return { success: false };
        }
        onStatusUpdate(`Scheduling copy user job (${jobId})...`);
        const scheduled = await scheduleCopyUserJob(jobId);
        if (!scheduled) {
            onStatusUpdate("Failed to schedule copy user job");
            return { success: false, jobId };
        }
        onStatusUpdate(`Monitoring copy user job (${jobId})...`);
        const success = await monitorCopyUserJob(jobId, (status, message) => {
            onStatusUpdate(message || `Job status: ${status}`);
        });
        return { success, jobId };
    }
    /**
     * Get source configuration for account mappings
     */
    async function getSourceConfig() {
        try {
            const token = getAuthToken();
            const response = await fetch(`${cspApiBaseUrl}/api/autoProvision/Source/${sourceId}`, {
                headers: createHeaders(token),
            });
            if (!response.ok) {
                console.error(`Get source config failed: ${response.status}`);
                return null;
            }
            return await response.json();
        }
        catch (error) {
            console.error("Error fetching source config:", error);
            return null;
        }
    }
    /**
     * Update source configuration with new account mapping
     */
    async function updateSourceConfig(config) {
        try {
            const token = getAuthToken();
            const response = await fetch(`${cspApiBaseUrl}/api/autoProvision/Source/${sourceId}`, {
                method: "PUT",
                headers: createHeaders(token, "application/json;charset=UTF-8"),
                body: JSON.stringify(config),
            });
            return response.ok;
        }
        catch (error) {
            console.error("Error updating source config:", error);
            return false;
        }
    }
    /**
     * Ensure account is mapped in the provisioning source
     * Returns whether the account was already mapped or newly added
     */
    async function ensureAccountMapping(accountId, accountName) {
        if (!accountId || !accountName) {
            return {
                success: false,
                wasAdded: false,
                message: "Missing account ID or name",
            };
        }
        try {
            // Get current source configuration
            const sourceConfig = await getSourceConfig();
            if (!sourceConfig) {
                return {
                    success: false,
                    wasAdded: false,
                    message: "Failed to fetch source configuration",
                };
            }
            // Find the Account field in source fields
            const accountField = sourceConfig.SourceFields.find((f) => f.FieldName === "Account");
            if (!accountField) {
                return {
                    success: false,
                    wasAdded: false,
                    message: "Account field not found in source configuration",
                };
            }
            // Check if account is already mapped (by InternalValue which is the AccountId)
            const existingMapping = accountField.Translations.find((t) => t.InternalValue === accountId || t.ExternalValue === accountName);
            if (existingMapping) {
                return {
                    success: true,
                    wasAdded: false,
                    message: "Account already mapped",
                    accountName: existingMapping.InternalDisplayName || accountName,
                };
            }
            // Add new mapping
            accountField.Translations.push({
                SourceFieldTranslationId: null,
                ExternalValue: accountName,
                InternalValue: accountId,
                InternalDisplayName: accountName,
                FacilityId: "",
                FacilityName: "",
            });
            // Update the source configuration
            const updated = await updateSourceConfig(sourceConfig);
            if (updated) {
                return {
                    success: true,
                    wasAdded: true,
                    message: "Account added to source",
                    accountName,
                };
            }
            else {
                return {
                    success: false,
                    wasAdded: false,
                    message: "Failed to update source configuration",
                };
            }
        }
        catch (error) {
            return {
                success: false,
                wasAdded: false,
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }
    return {
        uploadCSV,
        getJobStatus,
        monitorJobStatus,
        createCopyUserJob,
        scheduleCopyUserJob,
        getCopyUserJobStatus,
        monitorCopyUserJob,
        executeCopyReferenceUser,
        getSourceConfig,
        updateSourceConfig,
        ensureAccountMapping,
        environment,
        sourceId,
        provisioningBaseUrl,
    };
}
